import { useField } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import { components } from 'react-select'
import AsyncSelect from 'react-select/async'
import { Form } from 'react-bootstrap'
import styled from 'styled-components'
import uniqBy from 'lodash/uniqBy'
import debounce from 'lodash/debounce'
import { InfoItem, FormGroup, Divider } from 'components'
import Icon from 'components/Icon'
import DataGrid, { ColumnDataGrid } from 'components/DataGrid'
import { _, colors } from 'utils'
import { useEventSlug } from 'hooks'
import { getNodes } from 'graphql/utils'
import { getPerformers } from '../graphql/query'

const S = {
    PerformerInfo: styled(InfoItem)`
        &:hover div.hover-add-text {
            display: block;
        }
    `,
    AddText: styled.div`
        display: none;
        font-weight: bold;
        font-size: 14px;
        line-height: 16px;
        color: ${colors.primary};
    `
}

const customStyles = {
    menu: (provided) => ({
        ...provided,
        borderRadius: '0 0 4px 4px',
        marginTop: 0,
        zIndex: 100,
    }),
    option: (provided) => ({
        ...provided,
        padding: '0 16px',
        backgroundColor: 'transparent !important',
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    placeholder: () => ({
        paddingLeft: 8,
        fontSize: '14px',
        lineHeight: '16px',
        color: colors.grayTitle,
        opacity: 0.5
    }),
    control: (provided) => ({
        ...provided,
        height: 44,
    })
}

const DropdownIndicator = (
    props
) => (
    <components.DropdownIndicator {...props}>
        <Icon icon="search" color={colors.inputIcon} style={{ marginRight: 6 }} />
    </components.DropdownIndicator>
)

const renderOption = (user) => ({
    ...user,
    label: (
        <S.PerformerInfo
            name={`${user.name}`}
            description={user.jobtitle}
            image={user.mugshot}
            isBoldName={false}
        >
            <S.AddText className="hover-add-text">+ ADD</S.AddText>
        </S.PerformerInfo>
    )
})

interface Performer {
    id: number
    mugshot?: string
    name?: string
}
interface Props {
    name: string
    performers?: Performer[]
}

const PerformerInfo = ({ name, performers }: Props) => {
    const [
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        field,, helper
    ] = useField({ name })
    const eventSlug = useEventSlug()
    const [dataInTable, setDataInTable] = useState<Performer[]>(performers || [])
    const removePerformer = (id) => setDataInTable(dataInTable.filter((e) => e.id !== id))

    const columns: ColumnDataGrid[] = [
        {
            name: 'avatar',
            width: 6,
            render: (row) => (
                <InfoItem
                    name={row.name}
                    description={row.description}
                    image={row.mugshot}
                    style={{ padding: 0 }}
                    isBoldName={false}
                />
            )
        },
        { name: 'jobtitle', width: 5 },
        {
            name: 'actions',
            width: 1,
            render: (row) => (
                <Icon icon="trash" onClick={() => removePerformer(row.id)} />
            )
        },
    ]

    // @ts-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadOptions = useCallback(
        debounce((inputText, callback) => {
            getPerformers(eventSlug, inputText).then((response) => {
                const nodes = getNodes(response.data.event.performers)
                callback(nodes.map((e) => renderOption(e)))
            })
        }, 500), [eventSlug]
    )

    const handleUpdateData = (value: Performer) => {
        const updatedData = uniqBy([value, ...dataInTable], 'id') // Make sure no duplicate value
        setDataInTable(updatedData)
    }

    useEffect(() => {
        // After changing data in table, we should update the value of fields
        // This field here is the performerIds field that we would like to commit to API
        helper.setValue(dataInTable.map((e) => e.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataInTable])

    return (
        <>
            <FormGroup>
                <Form.Label>
                    {_('show.form.search_speaker')}
                </Form.Label>
                <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadOptions}
                    onChange={handleUpdateData}
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    placeholder={_('show.form.search_speaker_placeholder')}
                    controlShouldRenderValue={false}
                />
            </FormGroup>
            <Divider extend={10} />
            <DataGrid data={dataInTable} columns={columns} hideHeader requestStatus="succeeded" />
        </>
    )
}

export default PerformerInfo
